import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import {
  User,
  Mail,
  Phone,
  Copy,
  CheckCircle,
  Users,
  Shield,
  Loader2,
  Info,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePicker } from '@/components/shared/DatePicker'
import { employeeService } from '@/services/employeeService'
import { useAuthStore } from '@/store/authStore'

const contactNumberRegex = /^\d{10}$/

const employeeSchema = z.object({
  first_name: z.string().min(1, 'Please enter first name'),
  last_name: z.string().min(1, 'Please enter last name'),
  email: z.string().email('Please enter a valid email address'),
  contact_number: z
    .string()
    .min(1, 'Please enter contact number')
    .refine((value) => contactNumberRegex.test(value.trim()), 'Please enter a valid 10-digit contact number'),
  department: z.string().min(1, 'Please select department'),
  job_title: z.string().min(1, 'Please select job title'),
  role: z.string().min(1, 'Please select user role'),
  date_of_joining: z.date({ required_error: 'Please select joining date' }),
})

const EmployeeForm = ({ employee, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [newEmployee, setNewEmployee] = useState(null)
  const { user } = useAuthStore()
  const isEditMode = !!employee

  const form = useForm({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      contact_number: '',
      department: '',
      job_title: '',
      role: 'employee',
      date_of_joining: undefined,
    },
  })

  // Set default role when component mounts or pre-populate for edit
  useEffect(() => {
    if (isEditMode && employee) {
      form.reset({
        first_name: employee.first_name || '',
        last_name: employee.last_name || '',
        email: employee.user?.email || '',
        contact_number: employee.contact_number || '',
        department: employee.department || '',
        job_title: employee.job_title || '',
        role: employee.user?.role || 'employee',
        date_of_joining: employee.date_of_joining ? new Date(employee.date_of_joining) : undefined,
      })
    } else {
      form.setValue('role', 'employee')
    }
  }, [form, employee, isEditMode])

  const departments = [
    'Engineering',
    'Sales',
    'Marketing',
    'Human Resources',
    'Finance',
    'Operations',
    'Customer Support',
    'Product Management',
    'Design',
    'Quality Assurance'
  ]

  const jobTitles = [
    'Software Engineer',
    'Senior Software Engineer',
    'Product Manager',
    'Sales Representative',
    'Marketing Specialist',
    'HR Manager',
    'Financial Analyst',
    'Operations Manager',
    'Customer Success Manager',
    'UI/UX Designer',
    'QA Engineer',
    'DevOps Engineer',
    'Data Analyst',
    'Business Analyst'
  ]

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success('Copied to clipboard!')
    } catch (err) {
      toast.error('Failed to copy to clipboard')
    }
  }

  const onSubmit = async (values) => {
    setLoading(true)
    try {
      // Format date for API
      const formattedValues = {
        ...values,
        date_of_joining: dayjs(values.date_of_joining).format('YYYY-MM-DD')
      }

      if (isEditMode) {
        // Update existing employee
        const response = await employeeService.updateEmployee(employee.id, formattedValues)
        if (response.success) {
          toast.success('Employee updated successfully!')
          onSuccess && onSuccess()
        }
      } else {
        // Create new employee
        const response = await employeeService.createEmployee(formattedValues)
        if (response.success) {
          setNewEmployee(response.data.employee)
          setShowSuccess(true)
          toast.success('Employee created successfully!')
        }
      }
    } catch (error) {
      console.error(isEditMode ? 'Update employee error:' : 'Create employee error:', error)
      toast.error(error.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} employee`)
    } finally {
      setLoading(false)
    }
  }

  const handleCopyInvitationInfo = () => {
    if (newEmployee) {
      const info = `Employee ID: ${newEmployee.employee_id}\nEmail: ${newEmployee.email}\nRole: ${newEmployee.user?.role || 'employee'}\nStatus: Invitation sent`
      copyToClipboard(info)
    }
  }

  if (showSuccess && newEmployee) {
    return (
      <div className="text-center py-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-100 border border-emerald-200 mb-4">
          <CheckCircle className="h-6 w-6 text-emerald-600" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Employee Created Successfully!
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          An invitation email has been sent to the employee.
        </p>
        
        <div className="bg-muted/50 rounded-lg border border-border p-4 mb-4 text-left">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Employee ID</span>
              <span className="font-medium">{newEmployee.employee_id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email</span>
              <span className="font-medium">{newEmployee.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Role</span>
              <Badge variant="outline" className="capitalize">{newEmployee.user?.role || 'employee'}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Invitation Expires</span>
              <span className="font-medium text-xs">{new Date(newEmployee.user?.invitation_expires_at).toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-3">
          <Button variant="outline" onClick={handleCopyInvitationInfo}>
            <Copy className="mr-2 h-4 w-4" />
            Copy Info
          </Button>
          <Button
            onClick={() => {
              setShowSuccess(false)
              setNewEmployee(null)
              form.reset()
              onSuccess?.(newEmployee)
            }}
          >
            Done
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground">First Name</FormLabel>
                <FormControl>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Enter first name" className="pl-9 h-10" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground">Last Name</FormLabel>
                <FormControl>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Enter last name" className="pl-9 h-10" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground">Email Address</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="name@company.com"
                      className="pl-9 h-10"
                      disabled={isEditMode}
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contact_number"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground">Contact Number</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Enter 10-digit contact number"
                      className="pl-9 h-10"
                      inputMode="numeric"
                      maxLength={10}
                      {...field}
                      onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="department"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground">Department</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {departments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="job_title"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground">Job Title</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select job title" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {jobTitles.map(title => (
                      <SelectItem key={title} value={title}>{title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground">User Role</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select user role" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="employee">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        Employee
                      </div>
                    </SelectItem>
                    <SelectItem value="hr">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        HR Manager
                      </div>
                    </SelectItem>
                    {user?.role === 'admin' && (
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-muted-foreground" />
                          Administrator
                        </div>
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="date_of_joining"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground">Date of Joining</FormLabel>
                <FormControl>
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select joining date"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {!isEditMode && (
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg border border-border">
            <Info className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-foreground">Account Setup</p>
              <p className="text-muted-foreground">An invitation email will be sent to the employee with a secure link to set up their password.</p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEditMode ? 'Update Employee' : 'Create Employee'
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}

export default EmployeeForm
